import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useOrdersWebsocket from '@hooks/user/useOrdersWebsocket';
import useAdminOrder from '@hooks/admin/useAdminOrder';
import useAdminProducts from '@hooks/admin/useAdminProducts';
import { colFlex } from '@styles/flexStyles';
import AppContainer from '@components/common/container/AppContainer';
import { OrderStatus } from '@@types/index';
import TitledOrderStatusList from '@components/admin/order/realtime/TitledOrderStatusList';
import { useAtomValue } from 'jotai';
import { adminOrdersAtom } from '@jotai/admin/atoms';
import RightSidebarModal from '@components/common/modal/RightSidebarModal';
import OrderByProductList from '@components/admin/order/realtime/OrderByProductList';
import TableUsageSummary from '@components/admin/order/realtime/TableUsageSummary';
import styled from '@emotion/styled';
import { useWakeLock } from '@hooks/useWakeLock';

const ListContainer = styled.div`
  width: 95%;
  gap: 20px;
  ${colFlex({ justify: 'center', align: 'center' })}
`;

function AdminOrderRealtime() {
  useWakeLock();

  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { fetchTodayOrders } = useAdminOrder(workspaceId);
  const { fetchProducts } = useAdminProducts(workspaceId);

  const orders = useAtomValue(adminOrdersAtom);
  const notPaidOrders = orders.filter((order) => order.status === OrderStatus.NOT_PAID);
  const paidOrders = orders.filter((order) => order.status === OrderStatus.PAID);
  const servedOrders = orders.filter((order) => order.status === OrderStatus.SERVED);

  // 소켓이 거부되면 주문을 다시 불러온다. 토큰 만료가 원인이면 이 요청의 401로 토큰이 갱신되고, 갱신도 실패하면 로그인 화면으로 이동한다.
  useOrdersWebsocket(workspaceId, fetchTodayOrders);

  useEffect(() => {
    fetchTodayOrders();
    fetchProducts();
  }, []);

  return (
    <AppContainer useFlex={colFlex({ justify: 'start', align: 'center' })}>
      <ListContainer>
        <TableUsageSummary workspaceId={workspaceId} />
        <TitledOrderStatusList
          orders={notPaidOrders}
          orderStatus={OrderStatus.NOT_PAID}
          title={'주문 완료'}
          description={'주문이 완료되었으나, 결제가 아직 확인되지 않은 상태입니다.'}
        />
        <TitledOrderStatusList
          orders={paidOrders}
          orderStatus={OrderStatus.PAID}
          title={'결제 완료'}
          description={'결제가 확인되어 현재 조리 중인 주문입니다. 왼쪽부터 오래된 주문 순으로 표시됩니다.'}
        />
        <TitledOrderStatusList orders={servedOrders} orderStatus={OrderStatus.SERVED} title={'서빙 완료'} description={'서빙이 완료된 주문입니다.'} />
        <RightSidebarModal title="실시간 상품별 주문량" subtitle="*결제 완료 주문만 표시됩니다." useOpenButton={true}>
          <OrderByProductList orders={paidOrders} />
        </RightSidebarModal>
      </ListContainer>
    </AppContainer>
  );
}

export default AdminOrderRealtime;
